// The creator-collections listing page — eb-ui route `/cc/:collectionId/`
// (basePath `/cc`), a single custom-SSR page. The eb-ui Django backend
// pre-fetched ALL primary page data and injected it via `window.__SERVER_DATA__`
// (`CoreApplicationContext & CollectionProps & gaSettings`). Here that object is
// RECONSTRUCTED in `getServerSideProps`:
//
//   (a) TRACED public Core reads (gSSP → BFF bridge, DATA-FLOW-GUIDE §2 — request
//       context + existing endpoints, no new Core surface):
//         - collection metadata   GET /api/v3/collections/{id}/?expand=organizer,image
//         - first page of events  GET /api/v3/collections/{id}/events/public/… (upcoming+past)
//         - organizer's other collections  GET /api/v3/organizers/{id}/collections/…
//         - organizer follow status   GET /api/v3/organizers/{id}/?expand=follow_status
//   (b) request CONTEXT (locale/user) — forwarded straight through (no fetch).
//   (c) documented NON-CRITICAL degrade for Django-only computed fields with no
//       traced public oracle: `other_events_you_may_like` (personalization shelf —
//       EMPTY on the live guest oracle) and the SEO block (meta_title/canonical/
//       hreflang/json_ld) → a DERIVED `<title>` (sibling TTD precedent), never
//       invented.
//
// Every enrichment runs under `Promise.allSettled` (never `Promise.all` — the
// AbortError trap in memory.md). The collection read is the ONE critical field:
// a genuine 404/403/410 maps to the eb-ui `collection_moved` error render
// (`CollectionError`) — the page's second, verified render path.
//
// CREATOR_COLLECTIONS_FIXTURES=1 (allowlisted non-prod stages only) renders the
// page from the verified-live-oracle fixture with NO chrome service calls —
// the DoD smoke-render + visual-parity gates run here (no creds/VPN needed).
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { getShellServerSideProps } from '@eventbrite/valkyrie-app-shell/server';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import type { FooterProps, GlobalHeaderProps } from '@eventbrite/valkyrie-app-shell';
import Head from 'next/head';
import React from 'react';

import { FeatureFlagsProvider } from '@/lib/featureFlags';
import { getCollectionFixture, getCollectionMovedFixture } from '@/lib/fixtures/collection';
import { isFixtures } from '@/lib/bff/coreProxy';
import { reconstructServerData } from '@/lib/serverData';
import { buildCollectionSeo, type CollectionSeo } from '@/lib/seo';
import type { FeatureFlags } from '@/lib/featureFlags';
import { App } from '@/ported/app';
import type { CollectionServerData, ServerData } from '@/types';
import { EMPTY_GLOBAL_FOOTER, createDefaultLocaleInfo } from '@eventbrite/valkyrie-app-shell';
import type { GlobalFooterProps } from '@eventbrite/valkyrie-app-shell';

export interface CollectionPageProps {
    serverData: ServerData;
    featureFlags: FeatureFlags;
    header: GlobalHeaderProps;
    footer: FooterProps;
    locale: string;
    seo?: CollectionSeo;
}

/**
 * Extract the numeric collection id from the route segment. The live page uses
 * the SLUG form `/cc/practitioner-led-sessions-1457019/` (the numeric form
 * `/cc/1457019/` 301s to it); the id is the TRAILING numeric run of the segment.
 * A segment with no trailing number has no readable id → notFound (honest
 * degrade — we do not invent a collection for an unresolvable id).
 *
 * BUGBOT (High): previously this matched the FIRST digit run (`/(\d+)/`), so a
 * slug containing earlier digits (e.g. a year like `2024-talks-1457019`)
 * resolved the wrong Core id → gSSP loaded the wrong collection or the
 * moved/error path. Match the trailing run only.
 */
// The slug→id parser lives in `src/ported/lib` (a pure, dependency-free
// module) so the Bugbot regression test can import it without dragging in the
// page module's ESM-only app-shell import graph.
import { collectionIdFromSegment } from '@/ported/lib/collectionIdFromSegment';

/**
 * BUGBOT (High): `JSON.stringify(undefined)` returns `undefined`, so the
 * naive round-trip threw on the moved path (`seo` is `undefined` there).
 * Undefined → pass through as-is (the prop is optional).
 */
function jsonSafe<T>(value: T): T {
    if (value === undefined) {
        return value;
    }
    return JSON.parse(JSON.stringify(value));
}

function fallbackHeader(path: string): GlobalHeaderProps {
    return {
        env: { serverUrl: 'https://www.eventbrite.com' },
        request: { path },
    } as GlobalHeaderProps;
}
function fallbackFooter(req: GetServerSidePropsContext['req']): FooterProps {
    return {
        serverUrl: 'https://www.eventbrite.com',
        localeInfo: createDefaultLocaleInfo(req),
        isUserAuthenticated: false,
        globalFooterProps: EMPTY_GLOBAL_FOOTER as GlobalFooterProps,
    };
}

export const getServerSideProps: GetServerSideProps<CollectionPageProps> = async (
    context: GetServerSidePropsContext,
) => {
    const collectionId = collectionIdFromSegment(context.params?.collectionId);
    if (!collectionId) {
        return { notFound: true };
    }

    const requestLocale = context.req ? getLocaleFromRequest(context.req) : 'en_US';

    // Fixtures mode — render the verified-live-oracle page with NO chrome
    // service calls (drives smoke-render + visual-parity with no creds/VPN).
    // The SEO/301/404 logic below is exercised identically: the fixture
    // carries the same absolute_url + moved semantics as the real read.
    if (isFixtures()) {
        const shellResult = await getShellServerSideProps(context).catch(() => null);
        const movedRequested = collectionId === '9999999'; // dedicated moved fixture
        const previewRequested = String(context.query?.preview ?? '') === 'true';
        const fixture = movedRequested
            ? getCollectionMovedFixture()
            : getCollectionFixture();
        fixture.preview = previewRequested;
        const serverData = fixture;
        // Canonical-URL 301 (shared logic with real mode — see below).
        const fixtureUrl =
            'collection' in serverData ? serverData.collection?.url || '' : '';
        const fixtureExpected = (() => {
            try {
                if (!fixtureUrl) {
                    return null;
                }
                const m = new URL(fixtureUrl)
                    .pathname.replace(/\/+$/, '')
                    .match(/\/cc\/([^/]+)$/);
                return m ? m[1] : null;
            } catch {
                return null;
            }
        })();
        const fixtureSegment = context.params?.collectionId;
        if (
            typeof fixtureSegment === 'string' &&
            fixtureExpected &&
            fixtureSegment !== fixtureExpected
        ) {
            const fixtureQuery = (context.req.url || '').split('?')[1] || '';
            const fixtureDestination = fixtureQuery
                ? `${fixtureUrl}?${fixtureQuery}`
                : fixtureUrl;
            return {
                redirect: {
                    destination: fixtureDestination,
                    statusCode: 301,
                    base: false,
                },
            };
        }
        const movedPath = 'collection_moved' in serverData;
        if (movedPath) {
            context.res.statusCode = 404;
        }
        const seo = !movedPath
            ? buildCollectionSeo(serverData, {})
            : undefined;
        return {
            props: jsonSafe<CollectionPageProps>({
                serverData,
                featureFlags: (serverData.featureFlags || {}) as FeatureFlags,
                header: shellResult?.header ?? fallbackHeader(serverData.request.path || `/cc/${collectionId}/`),
                footer: shellResult?.footer ?? fallbackFooter(context.req),
                locale: requestLocale.replace('_', '-'),
                seo: jsonSafe<CollectionSeo | undefined>(seo),
            }),
        };
    }

    // Real mode — reconstruct the page from the traced public reads. The
    // collection read is the critical field; a 404/403/410 → the moved error.
    const { data, venueInfos } = await reconstructServerData(context, collectionId);
    const shellResult = await getShellServerSideProps(context).catch(() => null);

    // Canonical-URL 301: the live core page redirects ANY /cc/<segment>/ whose
    // segment is not "<slug>-<id>" (numeric id, wrong slug text, trailing
    // slash) to collection.absolute_url. Duplicate-content parity — derive
    // from the API's own canonical URL, never a hardcoded host.
    const absoluteUrl = 'collection' in data ? (data.collection?.url || '') : '';
    const segment = context.params?.collectionId;
    const expectedSegment = (() => {
        try {
            if (!absoluteUrl) {
                return null;
            }
            const pathname = new URL(absoluteUrl).pathname;
            // /cc/<slug>-<id> (or /xx_XX/cc/<slug>-<id> on localized hosts).
            // BUGBOT (Medium): absolute_url (and the live canonical) ends in
            // "/", which the original `[^/]+$` anchor rejected → expectedSegment
            // was always null and the 301 never fired. Strip the trailing
            // slash before matching.
            const m = pathname.replace(/\/+$/, '').match(/\/cc\/([^/]+)$/);
            return m ? m[1] : null;
        } catch {
            return null;
        }
    })();
    if (
        typeof segment === 'string' &&
        expectedSegment &&
        segment !== expectedSegment
    ) {
        // BUGBOT (High): Next.js only honours redirects from the `redirect`
        // gSSP result — a manual `Location` header + empty props still
        // renders the page body. Use the documented 301 form. Live core
        // (verified 2026-09-09) forwards the incoming query string on this
        // redirect (preview/autocreated/just_published survive), so append
        // the request's own query.
        const incomingQuery =
            (context.req.url || '').split('?')[1] || '';
        const destination = incomingQuery
            ? `${absoluteUrl}?${incomingQuery}`
            : absoluteUrl;
        return {
            redirect: {
                destination,
                statusCode: 301,
                base: false,
            },
        };
    }
    const movedPath = 'collection_moved' in data;
    if (movedPath) {
        // SEO parity: the live core page serves a nonexistent/moved collection
        // with HTTP 404 (the CollectionError render stays).
        context.res.statusCode = 404;
    }

    const seo = !movedPath
        ? buildCollectionSeo(
              data as CollectionServerData,
              venueInfos,
          )
        : undefined;

    return {
        props: jsonSafe<CollectionPageProps>({
            serverData: data,
            featureFlags: (data.featureFlags || {}) as FeatureFlags,
            header: shellResult?.header ?? fallbackHeader(`/cc/${collectionId}/`),
            footer: shellResult?.footer ?? fallbackFooter(context.req),
            locale: requestLocale.replace('_', '-'),
            seo: jsonSafe<CollectionSeo | undefined>(seo),
        }),
    };
};

export default function CollectionPage({
    serverData,
    featureFlags,
    header,
    footer,
    locale,
    seo,
}: CollectionPageProps) {
    const isMoved = 'collection_moved' in serverData && serverData.collection_moved;
    const collectionName = isMoved
        ? undefined
        : (serverData as CollectionServerData).collection?.name;
    // SEO: the legacy Django meta block (title/description/canonical/og/
    // hreflang/json_ld) is DERIVED in `src/lib/seo.ts` from the same data the
    // page already renders — every template verified against the live page.
    const pageTitle = seo?.title || (collectionName ? `${collectionName} | Eventbrite` : 'Collection | Eventbrite');

    return (
        <FeatureFlagsProvider value={featureFlags}>
            <Head>
                <title>{pageTitle}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                {seo?.description ? (
                    <meta name="description" content={seo.description} />
                ) : null}
                {seo?.canonical ? (
                    <link rel="canonical" href={seo.canonical} />
                ) : null}
                {!isMoved ? <meta name="robots" content="index, follow" /> : null}
                {seo?.canonical ? (
                    <meta property="og:url" content={seo.canonical} />
                ) : null}
                {seo ? <meta property="og:type" content="website" /> : null}
                <meta property="og:title" content={pageTitle} />
                {seo?.description ? (
                    <meta property="og:description" content={seo.description} />
                ) : null}
                {seo?.ogImage ? (
                    <meta property="og:image" content={seo.ogImage} />
                ) : null}
                {seo?.hreflangs.map((alt) => (
                    <link
                        key={alt.hreflang}
                        rel="alternate"
                        hrefLang={alt.hreflang}
                        href={alt.href}
                    />
                ))}
                {seo?.itemListJsonLd ? (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: seo.itemListJsonLd }}
                    />
                ) : null}
                {seo?.organizationJsonLd ? (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: seo.organizationJsonLd }}
                    />
                ) : null}
            </Head>
            <App
                serverData={serverData}
                header={header}
                footer={footer}
                locale={(serverData.env.localeInfo as { locale?: string }).locale || 'en_US'}
            />
        </FeatureFlagsProvider>
    );
}
